import { useEffect, useRef, useState, type ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'

import { Alert } from '@atoms'
import { Accordion } from '@components/atoms/Accordion'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import { PageLayout } from '@components/layouts/PageLayout'
import { currentLocale, scopedTranslation } from '@utils/I18n'

import { EmbedCodeBlock } from '@components/embeds/EmbedCodeBlock'
import { EmbedWorkspace, SettingsAccordionSection } from '@components/embeds/EmbedWorkspace'
import { PreviewShell } from '@components/embeds/PreviewShell'
import { ToggleRow } from '@components/embeds/ToggleRow'
import {
  RSVP_FIELD_NAMES,
  RSVP_FIELD_REQUIRES,
  NATIONBUILDER_PARTIAL,
  embedAddress,
  embedCode,
  nationbuilderConditionalSnippet,
  nationbuilderTemplateQuery,
  rsvpFormQuery,
  type RsvpFields,
} from '@utils/embeds/embedCode'
import { eventDates } from '@utils/embeds/eventDates'

const t = scopedTranslation('embeds.rsvp_form')
const tSample = scopedTranslation('embeds.rsvp_form.sample_form')

export type RsvpFormEvent = {
  id: string
  name: string
  startsAt: string | null
  endsAt: string | null
  timeZone: string
  formPath: string
  pageUrl: string | null
}

export type RsvpFormBuilderProps = {
  crm: string | null
  events: RsvpFormEvent[]
  unsyncedEventCount: number
  country: string
  embedHost: string
  templateFormPath: string
  resizeScriptUrl: string
}

const DEFAULT_FIELDS: RsvpFields = {
  show_address_fields: false,
  require_address_fields: false,
  show_phone_field: true,
  require_phone_field: false,
  allow_guests: true,
}

const HEIGHT_BEFORE_RESIZE = 640

type Where = 'template' | 'website'
type ShowOn = 'groups' | 'all'
type FieldSource = 'page' | 'fixed'

type Builder = {
  where: Where
  showOn: ShowOn
  fieldSource: FieldSource
  eventId: string | null
  fields: RsvpFields
}

function fieldFrom(value: string | null, fallback: boolean) {
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}

function builderFrom(search: string): Builder {
  const params = new URLSearchParams(search)
  const fields = { ...DEFAULT_FIELDS }
  RSVP_FIELD_NAMES.forEach((name) => {
    fields[name] = fieldFrom(params.get(name), DEFAULT_FIELDS[name])
  })
  return {
    where: params.get('where') === 'website' ? 'website' : 'template',
    showOn: params.get('show_on') === 'all' ? 'all' : 'groups',
    fieldSource: params.get('field_source') === 'fixed' ? 'fixed' : 'page',
    eventId: params.get('event'),
    fields,
  }
}

function builderQuery({ where, showOn, fieldSource, eventId, fields }: Builder, nationbuilder: boolean) {
  const params = new URLSearchParams(rsvpFormQuery(fields))
  if (eventId) params.set('event', eventId)
  if (nationbuilder) {
    params.set('where', where)
    params.set('show_on', showOn)
    params.set('field_source', fieldSource)
  }
  return params.toString()
}

function eventOption(event: RsvpFormEvent, locale: string) {
  const dates = eventDates({ starts_at: event.startsAt, ends_at: event.endsAt, time_zone: event.timeZone }, locale)
  return dates ? t('event.option', { name: event.name, when: dates.when }) : event.name
}

function LiveForm({ src }: { src: string }) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    const onMessage = ({ source, data }: MessageEvent) => {
      if (source !== frame.current?.contentWindow) return
      if (data?.type === 'resizeIframe' && typeof data.height === 'number' && Number.isFinite(data.height))
        setHeight(data.height)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <iframe
      ref={frame}
      src={src}
      title={t('preview_title')}
      // No allow-forms, so the preview can't send an RSVP.
      sandbox="allow-scripts"
      className="tw:block tw:w-full tw:border-0"
      style={{ height: height ?? HEIGHT_BEFORE_RESIZE }}
    />
  )
}

// #007966 is the real form's $primary (embeds.scss).
const SAMPLE_INPUT =
  'tw:block tw:w-full tw:rounded tw:border tw:border-[#ced4da] tw:bg-white tw:px-3 tw:py-1.5 tw:text-base tw:text-[#495057] tw:placeholder:text-[#6c757d] tw:focus:border-[#00f8d4] tw:focus:shadow-[0_0_0_0.2rem_rgba(0,121,102,0.25)] tw:focus:outline-hidden'
const SAMPLE_ROW = 'tw:grid tw:grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] tw:gap-x-2.5 tw:gap-y-4'

function SampleInput({ name, type = 'text' }: { name: string; type?: string }) {
  const label = tSample(name)
  return <input type={type} aria-label={label} placeholder={label} className={SAMPLE_INPUT} />
}

const SAMPLE_SHIFTS = [
  { role: 'setup', start: '9:00am', end: '10:00am', unassigned: 2, total: 2 },
  { role: 'stall', start: '10:00am', end: '1:00pm', unassigned: 3, total: 4 },
] as const

// It has shifts, so like the real form it doesn't ask for guests.
function SampleForm({ fields }: { fields: RsvpFields }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:rounded tw:border tw:border-[#dee2e6] tw:bg-[#f8f9fa] tw:p-6 tw:text-base tw:text-[#212529] tw:sm:p-12">
      <h3 className="tw:m-0 tw:text-[2rem] tw:font-medium tw:leading-tight">{tSample('title')}</h3>
      <p className="tw:m-0">
        {tSample('already_registered')}{' '}
        <a
          href="#cancel"
          onClick={(e) => e.preventDefault()}
          className="tw:text-[#007966] tw:hover:text-[#00473c] tw:hover:underline"
        >
          {tSample('cancel_link')}
        </a>
        .
      </p>
      <div className={SAMPLE_ROW}>
        <SampleInput name="first_name" />
        <SampleInput name="last_name" />
      </div>
      <div className={SAMPLE_ROW}>
        <SampleInput name="email" type="email" />
        {fields.show_phone_field && <SampleInput name="mobile" type="tel" />}
      </div>
      {fields.show_address_fields && (
        <>
          <SampleInput name="address1" />
          <SampleInput name="country" />
          <div className="tw:grid tw:grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] tw:gap-x-2.5 tw:gap-y-4">
            <SampleInput name="city" />
            <SampleInput name="state" />
            <SampleInput name="zip" />
          </div>
        </>
      )}
      <div className="tw:flex tw:flex-col tw:gap-4">
        <h4 className="tw:m-0 tw:text-2xl tw:font-medium tw:leading-tight">{tSample('shifts')}</h4>
        <p className="tw:m-0 tw:font-bold">{tSample('date')}</p>
        {SAMPLE_SHIFTS.map(({ role, start, end, unassigned, total }) => (
          <label
            key={role}
            className="tw:m-0 tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:text-base tw:font-normal tw:text-[#212529]"
          >
            <input type="checkbox" className="tw:mt-1.5 tw:cursor-pointer tw:accent-[#007966]" />
            <span>
              {tSample('shift_time', { start, end })} ({tSample('shift_availability', { unassigned, total })})
              <br />
              <span className="tw:font-bold">{tSample('role_label')}</span> {tSample(`sample_shifts.${role}`)}
            </span>
          </label>
        ))}
      </div>
      <button
        type="button"
        className="tw:self-start tw:rounded tw:border tw:border-[#007966] tw:bg-[#007966] tw:px-3 tw:py-1.5 tw:text-base tw:text-white tw:hover:border-[#005346] tw:hover:bg-[#005346]"
      >
        {tSample('submit')}
      </button>
    </div>
  )
}

const PARTIAL_FILE = `_${NATIONBUILDER_PARTIAL}.html`
const EVENT_TEMPLATE = 'pages_show_event.html'
const WIDE_EVENT_TEMPLATE = 'pages_show_event_wide.html'

function WithFileNames({ text }: { text: string }) {
  return (
    <>
      {text.split(/([\w.-]+\.html)/).map((part, index) =>
        part.endsWith('.html') ? (
          <code key={index} className="tw:rounded tw:bg-gray-cool-100 tw:px-1 tw:text-xs">
            {part}
          </code>
        ) : (
          part
        )
      )}
    </>
  )
}

function CodeStep({ number, text, children }: { number: number; text: ReactNode; children?: ReactNode }) {
  return (
    <li className="tw:flex tw:flex-col tw:gap-2">
      <p className="tw:m-0 tw:text-sm tw:text-gray-cool-700">
        <span className="tw:font-semibold tw:text-gray-cool-900">{number}. </span>
        {text}
      </p>
      {children}
    </li>
  )
}

const templateFiles = { file: EVENT_TEMPLATE, wide_file: WIDE_EVENT_TEMPLATE }

function TemplateCode({ code, showOn }: { code: string; showOn: ShowOn }) {
  const templateBlock = (templateCode: string) => (
    <EmbedCodeBlock
      label={t('code.template_label')}
      code={templateCode}
      copyLabel={t('code.template_copy')}
      copiedMessage={t('code.template_copied')}
    />
  )
  const step = (key: string, values: Record<string, string> = {}) => (
    <WithFileNames text={t(`code.steps.${key}`, { ...templateFiles, ...values })} />
  )

  if (showOn === 'all')
    return (
      <ol className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-5 tw:p-0">
        <CodeStep number={1} text={step('replace')}>
          {templateBlock(code)}
        </CodeStep>
        <CodeStep number={2} text={step('check_all')} />
      </ol>
    )

  return (
    <ol className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-5 tw:p-0">
      <CodeStep number={1} text={step('partial', { partial: PARTIAL_FILE })}>
        <EmbedCodeBlock
          label={t('code.partial_label')}
          code={code}
          copyLabel={t('code.partial_copy')}
          copiedMessage={t('code.partial_copied')}
        />
      </CodeStep>
      <CodeStep number={2} text={step('find')} />
      <CodeStep number={3} text={step('swap')}>
        {templateBlock(nationbuilderConditionalSnippet({ about: t('code.about'), placeholder: t('code.placeholder') }))}
      </CodeStep>
      <CodeStep number={4} text={step('check_groups')} />
    </ol>
  )
}

type EventOption = { value: string; label: string }

function PreviewEventPicker({
  options,
  eventId,
  onChange,
}: {
  options: EventOption[]
  eventId: string
  onChange: (eventId: string | null) => void
}) {
  return (
    <SelectField
      className="tw:w-56"
      name="rsvp_preview_event"
      label={t('event.preview_label')}
      value={eventId}
      onValueChange={onChange}
      options={options}
      search
    />
  )
}

function hostOf(url: string) {
  return url.replace(/^https?:\/\//, '')
}

export default function RsvpFormBuilder({
  crm,
  events,
  unsyncedEventCount,
  country,
  embedHost,
  templateFormPath,
  resizeScriptUrl,
}: RsvpFormBuilderProps) {
  const nationbuilder = crm === 'nationbuilder'
  const [path, search = ''] = usePage().url.split('?')
  const [builder, setBuilder] = useState(() => builderFrom(search))
  const { fields } = builder
  const onTemplate = nationbuilder && builder.where === 'template'
  const pageFields = onTemplate && builder.fieldSource === 'page'
  const event = events.find(({ id }) => id === builder.eventId) ?? events[0] ?? null
  const locale = `${currentLocale()}-${country.toUpperCase()}`

  const change = (patch: Partial<Builder>) => {
    const next = { ...builder, ...patch }
    setBuilder(next)
    router.replace({
      url: `${path}?${builderQuery(next, nationbuilder)}`,
      preserveState: true,
      preserveScroll: true,
    })
  }
  const setField = (name: keyof RsvpFields, on: boolean) => change({ fields: { ...fields, [name]: on } })
  const setEvent = (eventId: string | null) => change({ eventId })
  const eventOptions = events.map((option) => ({ value: option.id, label: eventOption(option, locale) }))

  // Each event page's own settings aren't known here, so the preview shows the defaults.
  const previewFields = pageFields ? DEFAULT_FIELDS : fields
  const query = rsvpFormQuery(previewFields)
  const websiteCode = event
    ? embedCode({
        address: embedAddress({ embedUrl: `${embedHost}${event.formPath}`, query }),
        resizeScriptUrl,
        title: t('code.title', { event: event.name }),
        height: null,
      })
    : ''
  const templateCode = embedCode({
    address: embedAddress({
      embedUrl: `${embedHost}${templateFormPath}`,
      query: nationbuilderTemplateQuery(pageFields ? null : fields),
    }),
    resizeScriptUrl,
    title: t('code.template_title'),
    height: null,
  })
  const unsynced = unsyncedEventCount > 0 ? t('event.unsynced', { count: unsyncedEventCount }) : null

  const eventSetting = event ? (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <SelectField
        name="rsvp_event"
        label={t('event.label')}
        value={event.id}
        onValueChange={setEvent}
        options={eventOptions}
        search
      />
      {unsynced && <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">{unsynced}</p>}
    </div>
  ) : (
    <Alert type="warning" size="sm" className="tw:text-xs">
      {unsynced ?? t('event.none')}
    </Alert>
  )

  const fieldToggles = RSVP_FIELD_NAMES.map((name) => {
    const parent = RSVP_FIELD_REQUIRES[name]
    return (
      <ToggleRow
        key={name}
        label={t(`fields.${name}`)}
        description={name === 'allow_guests' ? t('fields.allow_guests_description') : undefined}
        checked={fields[name]}
        onCheckedChange={(on) => setField(name, on)}
        disabled={parent ? !fields[parent] : false}
      />
    )
  })

  const previewNote = !event ? t('sample_note') : pageFields ? t('example_note') : t('live_note')

  return (
    <PageLayout.Root>
      <PageLayout.Header heading={t('heading')} icon="code-browser" subtitle={t('subtitle')} />
      <PageLayout.Body>
        <EmbedWorkspace
          settings={
            <Accordion type="multiple" defaultValue={['installation', 'event', 'fields', 'code']}>
              {nationbuilder && (
                <SettingsAccordionSection value="installation" title={t('sections.installation')}>
                  <RadioGroupField<Where>
                    label={t('where.label')}
                    value={builder.where}
                    onValueChange={(where) => change({ where })}
                    options={(['template', 'website'] as const).map((option) => ({
                      value: option,
                      label: t(`where.${option}`),
                      description: t(`where.${option}_hint`),
                    }))}
                  />
                </SettingsAccordionSection>
              )}

              {!onTemplate && (
                <SettingsAccordionSection value="event" title={t('sections.event')}>
                  {eventSetting}
                </SettingsAccordionSection>
              )}

              <SettingsAccordionSection value="fields" title={t('sections.fields')}>
                {onTemplate && (
                  <RadioGroupField<FieldSource>
                    label={t('field_source.label')}
                    value={builder.fieldSource}
                    onValueChange={(fieldSource) => change({ fieldSource })}
                    options={(['page', 'fixed'] as const).map((option) => ({
                      value: option,
                      label: t(`field_source.${option}`),
                    }))}
                  />
                )}
                {!pageFields && fieldToggles}
                <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">{t('fields.from_event')}</p>
              </SettingsAccordionSection>

              <SettingsAccordionSection value="code" title={t('sections.code')}>
                {onTemplate ? (
                  <>
                    <RadioGroupField<ShowOn>
                      label={t('show_on.label')}
                      value={builder.showOn}
                      onValueChange={(showOn) => change({ showOn })}
                      options={[
                        { value: 'groups', label: t('show_on.groups'), description: t('show_on.groups_hint') },
                        { value: 'all', label: t('show_on.all') },
                      ]}
                    />
                    <TemplateCode code={templateCode} showOn={builder.showOn} />
                  </>
                ) : (
                  <>
                    {event && (
                      <p className="tw:m-0 tw:text-sm tw:text-gray-cool-700">
                        {t('code.paste', { event: event.name })}
                      </p>
                    )}
                    <EmbedCodeBlock
                      label={t('code.label')}
                      code={websiteCode}
                      copyLabel={t('code.copy')}
                      copiedMessage={t('code.copied')}
                      emptyMessage={t('code.none')}
                    />
                  </>
                )}
              </SettingsAccordionSection>
            </Accordion>
          }
          preview={
            <PreviewShell
              host={onTemplate && event?.pageUrl ? hostOf(event.pageUrl) : t('preview_host')}
              control={
                onTemplate && event && events.length > 1 ? (
                  <PreviewEventPicker options={eventOptions} eventId={event.id} onChange={setEvent} />
                ) : undefined
              }
              note={previewNote}
            >
              <div className="tw:bg-white tw:p-4">
                {event ? <LiveForm src={`${event.formPath}?${query}`} /> : <SampleForm fields={previewFields} />}
              </div>
            </PreviewShell>
          }
        />
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
